import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client/core';
import { Auth } from '@aws-amplify/auth';
import { AccessType } from '@wb/core/enums/access-type';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CognitoUserDetails, WebBuyUserDetails } from '@wb/core/interfaces/auth';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AuthContext } from '../auth.context';
import { Account } from '../interfaces/account';
import {
  AccountPermission,
  AccountPermissionQueryResponse,
  ClientPermission,
  ClientPermissionInput,
  EditPermissionAndOptions,
  ParsedPermissionListInput,
  Permission,
  PermissionAndAccountUid,
  PermissionAndOptions,
  PermissionInput,
  PermissionList,
  PermissionQueryResponse,
} from '../interfaces/account-permission';
import {
  DeletePermission,
  DeleteUserPermissions,
  InsertPermission,
  InsertUserPermissions,
  UpdatePermission,
  UpdateUserPermissions,
} from '../schema/mutation-definitions/permissions.graphql';
import { UpdateUserSignIn } from '../schema/mutation-definitions/users_admin.graphql';
import {
  PermissionQuery,
  Permissions,
  PermissionsAndOptionsByUser,
  UserPermissions,
} from '../schema/query-definitions/permissions.graphql';
import { AccountService } from './account.service';
import { RoutingService } from './routing.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private _isAuthenticated = new ReplaySubject<boolean>(1);

  private user: any;

  authenticating$ = new BehaviorSubject<boolean>(false);

  permissionList: PermissionList | undefined;

  permissionAndOptionList$: BehaviorSubject<EditPermissionAndOptions | undefined> = new BehaviorSubject<
    EditPermissionAndOptions | undefined
  >(undefined);

  permissionList$ = new ReplaySubject<PermissionList>(1);

  activeAccountPermission$: BehaviorSubject<AccountPermission | undefined> = new BehaviorSubject<
    AccountPermission | undefined
  >(undefined);

  permissionsAndOptions$: BehaviorSubject<PermissionAndOptions | undefined> = new BehaviorSubject<
    PermissionAndOptions | undefined
  >(undefined);

  private userFullName?: string;

  private username?: string;

  userRoles: AccountPermission[] = [];

  userRoles$: BehaviorSubject<PermissionAndAccountUid | undefined> = new BehaviorSubject<
    PermissionAndAccountUid | undefined
  >(undefined);

  editRoleDone$: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);

  accountRoles$: BehaviorSubject<AccountPermission[] | undefined> = new BehaviorSubject<
    AccountPermission[] | undefined
  >(undefined);

  role$: BehaviorSubject<AccountPermission | undefined> = new BehaviorSubject<AccountPermission | undefined>(undefined);

  externalRole$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  roleLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private destroyed$: Subject<void> = new Subject<void>();

  constructor(
    private apollo: Apollo,
    private permissionsByUser: UserPermissions,
    private permissions: Permissions,
    private permission: PermissionQuery,
    private accountService: AccountService, // TODO: Remove anti-pattern.
    private userService: UserService, // TODO: Remove anti-pattern.
    private updatePermission: UpdatePermission,
    private insertPermission: InsertPermission,
    private deletePermission: DeletePermission,
    private permissionsAndOptionsByUser: PermissionsAndOptionsByUser,
    private deleteUserPermissions: DeleteUserPermissions,
    private insertUserPermissions: InsertUserPermissions,
    private updateUserPermissions: UpdateUserPermissions,
    private updateUserSignIn: UpdateUserSignIn,
    private authContext: AuthContext,
    private router: Router,
    private routing: RoutingService,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setTemporaryUser(user: CognitoUser | null) {
    this.user = user;
  }

  getTemporaryUser(): CognitoUser {
    return this.user;
  }

  setExternalRole(externalRole: boolean) {
    this.externalRole$.next(externalRole);
  }

  setTemporaryUsername(username: string) {
    this.username = username;
  }

  getTemporaryUsername(): string | undefined {
    return this.username;
  }

  get isAuthenticated() {
    return this._isAuthenticated.asObservable();
  }

  getUser(): CognitoUser | null {
    return this.authContext.getUser();
  }

  getUserFullName(): string | null {
    return this.userFullName || null;
  }

  async initIsAuthenticated(selectedAccountUid?: string): Promise<string | null> {
    return Auth.currentAuthenticatedUser()
      .then(async (cognitoUserData: CognitoUser) => {
        if ((cognitoUserData as any).preferredMFA !== 'NOMFA') {
          // If the selected account is different than the default account, return the default account
          const webbuyUserData = await this.setUser(cognitoUserData);
          return webbuyUserData.accountUid !== selectedAccountUid ? webbuyUserData.accountUid : null;
        } else {
          console.warn('No MFA configured for the user. Logging out...');
          this.logout();
          return null;
        }
      })
      .catch(() => {
        this._isAuthenticated.next(false);
        console.warn('[warn] not signed in');
        return null;
      });
  }

  async setUser(cognitoUserData: CognitoUser): Promise<WebBuyUserDetails> {
    this.authContext.setUser(cognitoUserData);

    const currentUser = await this.isCurrentUserActive();
    if (currentUser?.active) {
      this._isAuthenticated.next(true);
      this.fetchPermissionsByUser();
      this.updateLastLogin();
    } else {
      this.logout();
    }

    return currentUser;
  }

  async isCurrentUserActive(): Promise<WebBuyUserDetails> {
    return new Promise((resolve) => {
      this.userService.userDetails.pipe(takeUntil(this.destroyed$)).subscribe((webbuyUserDetails) => {
        if (webbuyUserDetails) {
          this.userFullName = `${webbuyUserDetails.first_name} ${webbuyUserDetails.last_name}`;
          const isActive = webbuyUserDetails.status?.trim().toLowerCase() === 'active';
          resolve({
            accountUid: webbuyUserDetails.accountUid,
            active: isActive,
            lastLogin: webbuyUserDetails.last_login,
          });
        } else {
          resolve({ accountUid: null, active: false, lastLogin: null });
        }
      });
    });
  }

  // Listens for changes in accountUID and sets permissions to match
  fetchPermissionsByUser(accountUid?: string) {
    if (this.getUserUid) {
      this.permissionsByUser
        .fetch({ userUid: this.getUserUid }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response: ApolloQueryResult<PermissionQueryResponse>) => {
            this.userRoles = response.data.permissionsByUser?.permissions;
            this.userRoles$.next({
              permissions: response.data.permissionsByUser?.permissions,
              accountUid: accountUid,
            });

            this.accountService.selectedAccount.subscribe({
              next: (account: Account | undefined) => {
                this.selectRoleAndSetPermissions(account?.uid);
              },
            });
          },
        });
    }
  }

  queryUserPermissionList(userUid: string) {
    this.permissionsByUser
      .fetch({ userUid: userUid }, { fetchPolicy: 'network-only' })
      .pipe(
        map((response) => response),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: any) => {
          this.activeAccountPermission$.next(response);
        },
      });
  }

  queryPermissionsAndOptionsByUser(userUid: string, adminUid: string) {
    this.permissionAndOptionList$.next(undefined);
    this.permissionsAndOptionsByUser
      .fetch(
        {
          userUid: userUid,
          adminUid: adminUid,
        },
        { fetchPolicy: 'network-only' },
      )
      .pipe(map((response) => response))
      .subscribe({
        next: (response: any) => {
          this.permissionAndOptionList$.next(response.data.permissionsAndOptionsByUser);
        },
      });
  }

  queryPermissionByAccount(accountUid: string) {
    this.permissions
      .fetch({ where: { accountUid } }, { fetchPolicy: 'no-cache' })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: ApolloQueryResult<AccountPermissionQueryResponse>) => {
          this.accountRoles$.next(response.data.permissions.permissions);
        },
      });
  }

  queryPermissionByUid(permissionUid: string) {
    this.roleLoading$.next(true);
    this.permission
      .fetch({ uid: permissionUid }, { fetchPolicy: 'network-only' })
      .pipe(
        map((response) => response),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: any) => {
          this.role$.next(response.data.permission.permission);
          this.roleLoading$.next(false);
        },
      });
  }

  editPermission(permissionUid: string, accountPermission: any, accountUid: string) {
    this.updatePermission
      .mutate({ uid: permissionUid, input: accountPermission })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          this.fetchPermissionsByUser(accountUid);
          return response;
        },
      });
  }

  createPermission(accountUid: string, accountPermission: any) {
    return this.insertPermission
      .mutate({ accountUid: accountUid, input: accountPermission })
      .pipe(takeUntil(this.destroyed$));
  }

  deleteAccountPermission(permissionUid: string) {
    this.deletePermission
      .mutate({ uid: permissionUid })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          return response;
        },
      });
  }

  async editUserRole(createList: any, editList: any, deleteList: any) {
    let sendEdit = undefined;
    let sendDelete = undefined;
    let sendCreate = undefined;

    if (createList.length) {
      this.insertUserPermissions
        .mutate({ input: createList })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response: any) => {
            return response;
          },
        });
    }

    if (editList.length) {
      this.updateUserPermissions
        .mutate({ input: editList })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response: any) => {
            return response;
          },
        });
    }

    if (deleteList.length) {
      this.deleteUserPermissions
        .mutate({ input: deleteList })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response: any) => {
            return response;
          },
        });
    }

    this.editRoleDone$.next([sendCreate, sendEdit, sendDelete]);
  }

  get isExternalRole(): boolean {
    return this.externalRole$.value;
  }

  get accountRoles(): Observable<AccountPermission[] | undefined> {
    return this.accountRoles$.asObservable();
  }

  get role(): Observable<AccountPermission | undefined> {
    return this.role$.asObservable();
  }

  get activeAccountPermission(): Observable<AccountPermission | undefined> {
    return this.activeAccountPermission$.asObservable();
  }

  get permissionAndOptions(): Observable<PermissionAndOptions | undefined> {
    return this.permissionsAndOptions$.asObservable();
  }

  get userPermission(): Observable<PermissionList | undefined> {
    return this.permissionList$.asObservable();
  }

  get userPermissionAndOptions(): Observable<EditPermissionAndOptions | undefined> {
    return this.permissionAndOptionList$.asObservable();
  }

  get userRoleList(): Observable<PermissionAndAccountUid | undefined> {
    return this.userRoles$.asObservable();
  }

  get editRoleDone(): Observable<any> {
    return this.editRoleDone$.asObservable();
  }

  get defaultPermission(): Permission {
    return {
      view: false,
      edit: false,
      add: false,
      delete: false,
    };
  }

  get defaultClientPermission(): ClientPermission {
    return {
      ...this.defaultPermission,
      limitAssigned: false,
      editBuildFinishDeal: false,
      editNetDesker: false,
      assignSalesperson: false,
    };
  }

  private selectRoleAndSetPermissions(accountUid?: string) {
    this.roleLoading$.next(true);
    const externalRole = this.userRoles.find((role) => role.access_type === AccessType.EXTERNAL);
    if (externalRole?.access_type) {
      this.externalRole$.next(externalRole?.access_type === AccessType.EXTERNAL);
    }
    if (!accountUid && !externalRole) {
      const firstInternalRole = this.userRoles.find((role) => role.access_type === AccessType.INTERNAL);
      if (firstInternalRole?.accountUid) {
        this.accountService.selectAccount(firstInternalRole.accountUid);
      }
      return;
    }

    const permissionList = this.generatePermissionList(accountUid);
    if (permissionList) {
      this.permissionList = permissionList;
      this.permissionList$.next(this.permissionList);
      this.roleLoading$.next(false);
    }
  }

  private generatePermissionList(accountUid?: string): PermissionList | undefined {
    const internalRole: AccountPermission | undefined = accountUid
      ? this.userRoles.find((role) => role.accountUid === accountUid && role.access_type === AccessType.INTERNAL)
      : undefined;

    const externalRole: AccountPermission | undefined = this.userRoles.find(
      (role) => role.access_type === AccessType.EXTERNAL,
    );

    if (externalRole) {
      return this.getPermissionList(externalRole);
    } else if (internalRole) {
      return this.getPermissionList(internalRole);
    }

    console.warn('No permission!');
    return undefined;
  }

  private mapToPermission(input: PermissionInput): Permission {
    return {
      view: !!input?.view,
      edit: !!input?.edit,
      add: !!input?.add,
      delete: !!input?.delete,
    };
  }

  private mapToClientPermission(input: ClientPermissionInput): ClientPermission {
    return {
      ...this.mapToPermission(input),
      limitAssigned: !!input?.limitAssigned,
      editBuildFinishDeal: !!input?.editBuildFinishDeal,
      editNetDesker: !!input?.editNetDesker,
      assignSalesperson: !!input?.assignSalesperson,
    };
  }

  mapToPermissionInput(input: Permission): PermissionInput {
    return {
      view: input?.view ? 1 : 0,
      edit: input?.edit ? 1 : 0,
      add: input?.add ? 1 : 0,
      delete: input?.delete ? 1 : 0,
    };
  }

  mapToClientPermissionInput(input: ClientPermission): ClientPermissionInput {
    return {
      ...this.mapToPermissionInput(input),
      limitAssigned: input?.limitAssigned && input?.view ? 1 : 0,
      editBuildFinishDeal: input?.editBuildFinishDeal ? 1 : 0,
      editNetDesker: input?.editNetDesker && input?.editBuildFinishDeal ? 1 : 0,
      assignSalesperson: input?.assignSalesperson && input?.view ? 1 : 0,
    };
  }

  private getPermissionList(input: AccountPermission): PermissionList {
    const selectedPermission: ParsedPermissionListInput = JSON.parse(input.permission);

    return {
      dashboard: this.mapToPermission(selectedPermission?.dashboard),
      account: this.mapToPermission(selectedPermission?.account),
      groups: this.mapToPermission(selectedPermission?.groups),
      users: this.mapToPermission(selectedPermission?.users),
      clients: this.mapToClientPermission(selectedPermission?.clients),
      inventory: this.mapToPermission(selectedPermission?.inventory),
      report: this.mapToPermission(selectedPermission?.report),
      settings: this.mapToPermission(selectedPermission?.settings),
      support: this.mapToPermission(selectedPermission?.support),
      system: this.mapToPermission(selectedPermission?.system),
    };
  }

  get getUserUid() {
    return this.authContext.getUid();
  }

  async signIn(): Promise<CognitoUserDetails> {
    const currentUser = await this.setUser(await Auth.currentAuthenticatedUser());
    if (currentUser?.active) {
      this.updateLastLogin();
    }
    return currentUser;
  }

  private updateLastLogin() {
    this.updateUserSignIn
      .mutate({ uid: this.getUserUid })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          return response;
        },
      });
  }

  async logout() {
    await Auth.signOut();

    this._isAuthenticated.next(false);
    this.apollo.client.stop();
    this.apollo.client.clearStore();

    localStorage.removeItem('accountUid');
    await this.router.navigate(['auth', 'signIn']);
  }

  async ifSignedInAsync(): Promise<boolean> {
    try {
      await Auth.currentAuthenticatedUser();

      // Routing only resets after the user has been navigated to a non-auth url
      if (this.router.navigated && !this.router.url.includes('/auth')) {
        this.routing.startUrl = undefined; // Setting this to undefined prevents routing from saving even after logging out
        this.routing.assignLeadToUser = false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  getDefaultEntryPage(accountUid: string, permissions: any): string[] {
    if (permissions) {
      const permission: any = Object.keys(permissions).find(
        (key: string) => permissions?.[key as keyof PermissionList].view,
      );

      switch (permission) {
        case 'dashboard':
          return ['dashboard', accountUid];
        case 'clients':
          return ['accounts', accountUid, 'clients'];
        case 'inventory':
          return ['accounts', accountUid, 'inventory'];
        case 'account':
          return ['accounts'];
        case 'groups':
          return ['groups', accountUid];
        case 'users':
          return ['users', accountUid, 'list'];
        case 'report':
          return ['reports', accountUid];
        case 'settings':
          return ['account-setup', accountUid];
        case 'support':
          return ['support/report-problem'];
        case 'system':
          return ['system-settings'];
      }
    }

    return ['default'];
  }
}
